<template>
  <div class="v2-home">
    <app-section theme="dark grid" class="top-section">
      <hero :key="selectedLanding?.id || 'hero'" :videos="heroVideos || []" :cta-label="ctaLabel" />
      <our-models :models="models" />
    </app-section>
    <app-section theme="nude" :wave-top="true" class="video-categories-section">
      <video-categories :landing-id="selectedLanding?.id ?? null" :videos="categoryVideos" :tags="videoTags" />
    </app-section>
    <app-section theme="blue" class="our-story-section">
      <our-story />
    </app-section>
    <app-section theme="blue" class="reviews-section">
      <reviews />
    </app-section>
    <app-section theme="blue" class="steps-create-porn-section">
      <steps-create-porn />
    </app-section>
    <app-section theme="nude" class="boring-porn">
      <boring-porn />
      <featured />
    </app-section>
    <app-section theme="dark" :wave-top="true" class="currently-on-site-section">
      <currently-on-site v-if="onSiteVideo" :key="onSiteVideo?.id || 'currently'" :video="onSiteVideo" />
    </app-section>
    <app-section theme="light-lilac" class="our-advantages-section">
      <our-advantages />
    </app-section>
  </div>
</template>

<script setup lang="ts">
import OurModels from "@/components/v2/our-models/index.vue";
import AppSection from "@/components/v2/section/index.vue";
import Hero from "@/components/v2/hero/index.vue";
import { useI18n } from "vue-i18n";
import { useLandingStore } from "@/store/landing";
import { useModalStore } from "@/store/modal";
import { computed, defineAsyncComponent, nextTick, onMounted, PropType, watch } from "vue";
import { amplitudePageViewTrack } from "@/utils/amplitude";
import { useRoute, useRouter } from "vue-router";
import { Landing } from "@/interfaces/Landing.ts";
import { appendPreloadImage } from "@/utils/appendPreloadImage.ts";
import rainbowTop from "@/assets/v2/images/rainbow-top.svg?url";
import rainbow from "@/assets/v2/images/rainbow.svg?url";

defineOptions({
  name: "landing-home",
});

const props = defineProps({
  landing: {
    type: Object as PropType<Landing>,
    default: null,
  },
});

const landingStore = useLandingStore();
const modalStore = useModalStore();

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();

const OurStory = defineAsyncComponent(() => import("@/components/v2/our-story/index.vue"));
const StepsCreatePorn = defineAsyncComponent(() => import("@/components/v2/steps-create-porn/index.vue"));
const CurrentlyOnSite = defineAsyncComponent(() => import("@/components/v2/currently-on-site/index.vue"));
const OurAdvantages = defineAsyncComponent(() => import("@/components/v2/our-advantages/index.vue"));
const Reviews = defineAsyncComponent(() => import("@/components/v2/reviews/index.vue"));
const BoringPorn = defineAsyncComponent(() => import("@/components/v2/boring-porn/index.vue"));
const Featured = defineAsyncComponent(() => import("@/components/v2/featured/index.vue"));
const VideoCategories = defineAsyncComponent(() => import("@/components/v2/video-categories/index.vue"));

const defaultLanding = computed(() => landingStore.getDefaultLandingPage(locale.value));
const selectedLanding = computed(() => props.landing || defaultLanding.value);
const heroVideos = computed(() => (selectedLanding.value ? selectedLanding.value.hero_videos : []));
const ctaLabel = computed(() => selectedLanding.value?.cta_text || undefined);
const models = computed(() => (selectedLanding.value ? selectedLanding.value.models : []));
const videoTags = computed(() => (selectedLanding.value ? selectedLanding.value.tags : []));
const categoryVideos = computed(() => (selectedLanding.value ? selectedLanding.value.videos : []));
const onSiteVideo = computed(() => (selectedLanding.value ? selectedLanding.value.currently_on_site : null));

watch(locale, () => {
  loadLanding();
});

function scroll() {
  if (!route.hash) {
    return;
  }

  const hash = route.hash.slice(1);

  nextTick(() => {
    const el = document.getElementById(hash);

    if (!el) {
      return;
    }

    el.scrollIntoView({
      behavior: "smooth",
    });
  });
}

async function loadLanding() {
  if (!props.landing && !defaultLanding.value) {
    await landingStore.fetchLandingPage({ lang: locale.value });

    route.meta.landing_id = selectedLanding.value?.id;
  }

  scroll();
}

// Lifecycle hooks
onMounted(async () => {
  appendPreloadImage(rainbowTop, "preload-v2-landing-rainbow-top");
  appendPreloadImage(rainbow, "preload-v2-landing-rainbow");
  await loadLanding();
  amplitudePageViewTrack();

  router.isReady().then(() => {
    if (route.hash === "#log_in") {
      modalStore.showModal({ id: "modal-login" });
    }
  });
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
